export default `# Installing Node

To continue your React adventures you have to install *Node.js*.

Node.js is a server-side platform and a set of tools to build web applications with JavaScript.
It's open source and it's free.

You may be wondering: Why on earth do I need a server-side platform if I'm building a User Interface?

Outside of the environments like a *CodeSandbox*, you need a platform that will build your application and serve it to the browser.

To build an application, Node has to take all application components and external packages (dependencies), such as \`react\` and \`react-dom\` and
bundle them into one package. Then and only then will your application be ready to be sent to the browser.

On top of that, React uses ES6 – the newest version of JavaScript. Not all browsers support all ES6 features. That's why the ES6 code has to be transpiled
(translated) into an older version of Javascript (ES5).

To transpile the ES6 code to ES5, you will have to use [Babel](https://babeljs.io/). But worry not. The boilerplate will set it up for you in the background.

Node.js comes also with a package manager called \`npm\` (Node package manager). The \`npm\` gives you an easy access to a huge registry of packages
built by other JavaScript developers.

Thanks to the expanding open source community around Node, the registry of packages grows rapidly, delivering
more and more solutions that, like Lego blocks, can be attached to your applications.

## Node installation

To install Node, head to the [download page](https://nodejs.org/en/download/) and choose your version of the operating system:

![Node installation](https://sweetpumpkins.codecamps.com/node-001.jpg)

Once the installation process finishes, go to your terminal and check if everything works properly:

\`\`\`plainJavascript
node -v
npm -v
\`\`\`

In your console, you should see similar or higher versions:

![Node and npm version check in console](https://sweetpumpkins.codecamps.com/node-version-check.jpg)

## The boilerplate

Setting up a pipeline for the build process of the application is tedious. Instead of repeating
it from scratch every time you want to build a new project, you can use a boilerplate.

The boilerplate is an almost empty project wired up for you.

There are many boilerplates that you can take into account to build the React application on top of, but two stand out:
- the [React Boiler Plate](https://github.com/react-boilerplate/react-boilerplate), authored by Max Stoiber and extended by the vibrant React community
- Create React App, by Facebook

In this course, we will use Create React App.

Go to the console and install the \`create-react-app\` globally (\`-g\`) using the \`npm\`:

\`\`\`plainJavascript
npm install -g create-react-app
\`\`\`


Next, create a new project – \`sweet-pumpkings\`. It may take a few minutes to install all dependencies:

\`\`\`plainJavascript
create-react-app sweet-pumpkins
\`\`\`

Once the installation finishes you will see the following text:

![](https://sweetpumpkins.codecamps.com/npm-install-yarn-min.jpg)

Wait. But what is \`yarn\`?

It's another package manager, like \`npm\`. It was built by Facebook, Google, Exponent, and Tilde.

The purpose of \`yarn\` is exactly the same as that of \`npm\`. So why you should care?

The \`yarn\` is faster. And it doesn't have the flaws which affect the \`npm\`.
The coolest thing about the \`yarn\`. It still uses the npm registry to give you an access to thousands of available packages on the \`NPM\` registry.

You don't have to install and use it to make \`create-react-app\` work.

Here is a great [blog post](https://code.facebook.com/posts/1840075619545360) that explains why it was built
and why it might be a good choice for you.

I know it may feel like a lot of setup but you have to do it only once.

Let's jump on the \`yarn\` train and ask the old package manager to install the new one globally:

\`\`\`plainJavascript
npm install -g yarn
\`\`\`

You are now all set!

Let's dive into the \`sweet-pumpkins\` project directory and open it in your editor of choice. Here, I am using the [Atom](https://atom.io/) editor:

\`\`\`plainJavascript
cd sweet-pumpkins
atom .
\`\`\`




`;



// WEBPACK FOOTER //
// ./src/book/chapters/node-js.js